import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { map, Observable, startWith, switchMap } from "rxjs";
import { AoIconUrlPipe } from "~app/common/pipes/ao-icon-url.pipe";
import { ApiClientService } from "~app/common/services/api-client.service";
import { prologLink } from "~app/common/utils/prolog-link";
import { Alert, GetVirkeskopAlertsQueryResult } from "~app/features/virkeskop/services/virkeskop.service";
import { DashboardService } from "../../dashboard.service";
import { AlertItemTemplateDirective } from "../alert-list/alert-item-template.directive";
import { AlertItem, AlertItemLink, AlertListComponent } from "../alert-list/alert-list.component";
import { MessageScopes } from "../alert-list/message-scopes";

const mapVirkeskopAlertsQueryResultToAlertItems = (result: GetVirkeskopAlertsQueryResult): AlertItem[] => result.alerts.map(alert => ({
    title: alert.title,
    message: alert.message,
    timestamp: new Date(alert.occured),
    severity: alert.severity,
    cssClass: alert.metadata?.cssClass,
    links: mapVirkeskopAlertLinks(result, alert),
    metadata: alert.metadata
}));

const mapVirkeskopAlertLinks = (result: GetVirkeskopAlertsQueryResult, alert: Alert): AlertItemLink[] => {
    const links: AlertItemLink[] = [
        {
            text: alert.subEntityLinkName,
            resource: `virkeskop/avtal/${result.virkeskopsavtalId}`,
            queryParams: {
                kontraktId: alert.subEntityId?.toString()
            }
        }
    ];

    if (alert.metadata?.kalla === "VirkeskopArbetsorderstatusAlertProvider") {
        links.push({
            text: alert.metadata.arbetsorderBeteckning,
            resource: `/planera/${alert.metadata.arbetsorderId}`,
            isExternal: false,
            title: "Arbetsorder"
        });
    } else if (alert.metadata?.arbetsorderBeteckning) {
        links.push({
            text: alert.metadata.arbetsorderBeteckning,
            resource: prologLink(alert.metadata.arbetsorderBeteckning),
            isExternal: true,
            title: "Arbetsorder i Prolog"
        });
    }

    return links;
};

@Component({
    selector: "rask-avverkning-list",
    imports: [
        AlertListComponent,
        AlertItemTemplateDirective,
        AoIconUrlPipe
    ],
    templateUrl: "./avverkning-list.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./avverkning-list.component.scss"
})
export class AvverkningListComponent {
    protected events$: Observable<AlertItem[]>;

    private readonly apiClientService = inject(ApiClientService);
    private readonly dashboardService = inject(DashboardService);

    private readonly userId$ = this.dashboardService.user$.pipe(
        map(user => user.id)
    );

    constructor() {
        this.events$ = this.userId$.pipe(
            switchMap(userId => this.getVirkeskopAlerts(userId))
        );
    }

    private getVirkeskopAlerts(userId: number): Observable<AlertItem[]> {
        return this.apiClientService.get<GetVirkeskopAlertsQueryResult[]>(`virkeskoperror/${userId}?scope=${MessageScopes.Avverkning}`).pipe(
            map(res => res.flatMap(x => mapVirkeskopAlertsQueryResultToAlertItems(x))),
            startWith(null)
        );
    }
}
